import React from "react";
import {
  ThemeProvider,
  createTheme,
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Stack,
  Link as MuiLink,
  Divider,
  Card,
  CardContent,
  Button,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Description as FileIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  AdminPanelSettings as AdminIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";

// ============================================
// Modern Dashboard Theme (MUI5)
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a", // deep navy from spec
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2rem",
      fontWeight: 700,
      color: "#1f2937",
      "@media (max-width: 600px)": {
        fontSize: "1.5rem",
      },
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
      "@media (max-width: 600px)": {
        fontSize: "1.1rem",
      },
    },
    h6: {
      "@media (max-width: 600px)": {
        fontSize: "1rem",
      },
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
      "@media (max-width: 600px)": {
        fontSize: "0.813rem",
      },
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          justifyContent: "space-between",
          padding: "12px 16px",
          borderRadius: 6,
          "@media (max-width: 600px)": {
            padding: "8px 12px",
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          "@media (max-width: 600px)": {
            paddingLeft: "16px",
            paddingRight: "16px",
          },
        },
      },
    },
  },
});

// ============================================
// Styled Components
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  minHeight: "90px",
  padding: "16px 40px",
  display: "flex",
  alignItems: "center",
  width: "100%",
  boxSizing: "border-box",
  [theme.breakpoints.down("sm")]: {
    padding: "12px 16px",
    minHeight: "80px",
  },
}));

const LogoCircle = styled(Box)(({ theme }) => ({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  boxSizing: "border-box",
  marginRight: "16px",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    width: "45px",
    height: "45px",
    fontSize: "16px",
    marginRight: "12px",
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 24px -8px rgba(0,0,0,0.15)",
    borderColor: "#243b5a",
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiCardContent-root": {
      padding: "16px !important",
    },
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: "40px",
  height: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#243b5a",
  flexShrink: 0,
  "& svg": {
    fontSize: "28px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "24px",
    },
  },
}));

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  display: "inline-block",
  "&:hover": {
    color: "#ffffff",
  },
});

const ContentWrapper = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
});

// ============================================
// Main Component
// ============================================
const DeltaGovPortal: React.FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const history = useHistory();

  // Navigation handlers
  const handleNavigation = (route: string) => {
    console.log(`Navigating to: ${route}`);
    history.push(route);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          bgcolor: "background.default",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // Prevents double scrollbars
        }}
      >
        {/* HEADER - Fixed at top */}
        <Header component="header">
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <LogoCircle>⚖️</LogoCircle>
            <Box sx={{ minWidth: 0, // Allows text truncation if needed
              }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: "#cbd5e1", 
                  letterSpacing: "0.5px", 
                  display: "block",
                  fontSize: isMobile ? "0.65rem" : "0.75rem",
                }}
              >
                DELTA STATE GOVERNMENT
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: "#ffffff", 
                  fontWeight: 600, 
                  lineHeight: 1.2,
                  fontSize: isMobile ? "0.95rem" : "1.25rem",
                }}
              >
                Ministry of Primary Education
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "#d1d5db",
                  fontSize: isMobile ? "0.75rem" : "0.875rem",
                }}
              >
                Official Portal
              </Typography>
            </Box>
          </Box>
        </Header>

        {/* MAIN CONTENT - Scrollable area */}
        <ContentWrapper
          component="main"
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, sm: 3, md: 0 },
          }}
        >
          <Container 
            maxWidth="lg" 
            sx={{ 
              py: { xs: 3, md: 6 },
            }}
          >
            <Grid container spacing={isMobile ? 3 : 4}>
              {/* LEFT COLUMN (70%) */}
              <Grid item xs={12} md={8}>
                <Typography 
                  variant="h1" 
                  sx={{ 
                    fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                    fontWeight: 700,
                  }}
                >
                  Online Services
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: "#6b7280", 
                    mt: 0.5,
                    fontSize: { xs: "0.813rem", sm: "0.875rem" },
                  }}
                >
                  Select a service to proceed
                </Typography>
                <Divider sx={{ my: { xs: 1.5, md: 2 }, borderWidth: 1 }} />

                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: "block", 
                    mt: { xs: 2, md: 3 }, 
                    mb: { xs: 1.5, md: 2.5 },
                  }}
                >
                  AVAILABLE SERVICES
                </Typography>

                {/* CARD GRID */}
                <Grid container spacing={isMobile ? 2 : 2.5}>
                
                      {/* Card 3 - School Portal Access */}
                  <Grid item xs={12} sm={6}>
                    <StyledCard>
                      <CardContent sx={{ display: "flex", gap: { xs: 1.5, sm: 2 }, p: { xs: 2, sm: 3 } }}>
                        <IconWrapper>
                          <SchoolIcon />
                        </IconWrapper>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontSize: { xs: "1rem", sm: "1.125rem" },
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            School Portal Access
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: "#6b7280",
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                          >
                            Authorized access for school administrators and registration management
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => handleNavigation("/login_school")}
                            endIcon={<ChevronRightIcon />}
                            sx={{ 
                              mt: 1, 
                              p: 0, 
                              color: "#1d4ed8", 
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                              "& .MuiButton-endIcon": {
                                marginLeft: "4px",
                                "& svg": {
                                  fontSize: { xs: "16px", sm: "20px" },
                                },
                              },
                            }}
                          >
                            School Login
                          </Button>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>

                  {/* Card 2 - CIE Student Inspector */}
                  <Grid item xs={12} sm={6}>
                    <StyledCard>
                      <CardContent sx={{ display: "flex", gap: { xs: 1.5, sm: 2 }, p: { xs: 2, sm: 3 } }}>
                        <IconWrapper>
                          <PeopleIcon />
                        </IconWrapper>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontSize: { xs: "1rem", sm: "1.125rem" },
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            CIE Student Inspector
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: "#6b7280",
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                          >
                            Browse and manage student records by Local Government Area
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => handleNavigation("/login_cie")}
                            endIcon={<ChevronRightIcon />}
                            sx={{ 
                              mt: 1, 
                              p: 0, 
                              color: "#1d4ed8", 
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                              "& .MuiButton-endIcon": {
                                marginLeft: "4px",
                                "& svg": {
                                  fontSize: { xs: "16px", sm: "20px" },
                                },
                              },
                            }}
                          >
                            Inspector Login
                          </Button>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>

              
  {/* Card 1 - Check Primary Result */}
                  <Grid item xs={12} sm={6}>
                    <StyledCard>
                      <CardContent sx={{ display: "flex", gap: { xs: 1.5, sm: 2 }, p: { xs: 2, sm: 3 } }}>
                        <IconWrapper>
                          <FileIcon />
                        </IconWrapper>
                        <Box sx={{ minWidth: 0, // Allow text wrapping
                          }}>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontSize: { xs: "1rem", sm: "1.125rem" },
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            Check Primary Result
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: "#6b7280",
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                          >
                            Access and verify official primary school examination results and certificates
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => handleNavigation("/login_check_result")}
                            endIcon={<ChevronRightIcon />}
                            sx={{ 
                              mt: 1, 
                              p: 0, 
                              color: "#1d4ed8", 
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                              "& .MuiButton-endIcon": {
                                marginLeft: "4px",
                                "& svg": {
                                  fontSize: { xs: "16px", sm: "20px" },
                                },
                              },
                            }}
                          >
                            Access Portal
                          </Button>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                  {/* Card 4 - Admin Login */}
                  <Grid item xs={12} sm={6}>
                    <StyledCard>
                      <CardContent sx={{ display: "flex", gap: { xs: 1.5, sm: 2 }, p: { xs: 2, sm: 3 } }}>
                        <IconWrapper>
                          <AdminIcon />
                        </IconWrapper>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontSize: { xs: "1rem", sm: "1.125rem" },
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            Admin Login
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: "#6b7280",
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                          >
                            Secure access for ministry administrators and system management
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => handleNavigation("/login_admin")}
                            endIcon={<ChevronRightIcon />}
                            sx={{ 
                              mt: 1, 
                              p: 0, 
                              color: "#1d4ed8", 
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                              "& .MuiButton-endIcon": {
                                marginLeft: "4px",
                                "& svg": {
                                  fontSize: { xs: "16px", sm: "20px" },
                                },
                              },
                            }}
                          >
                            Admin Login
                          </Button>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                </Grid>
              </Grid>

              {/* RIGHT COLUMN (30%) - Hidden on mobile? Actually let's show but stack */}
              <Grid item xs={12} md={4}>
                <Stack spacing={isMobile ? 2 : 2.5}>
                  {/* NOTICE BOX */}
                  <Paper sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                      NOTICE
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      For comprehensive primary education portal services, visit the official ministry website at{" "}
                      <MuiLink 
                        href="#" 
                        sx={{ 
                          color: "#1d4ed8", 
                          textDecoration: "underline",
                          wordBreak: "break-all",
                        }}
                      >
                        www.dsgmope.ng
                      </MuiLink>
                    </Typography>
                  </Paper>

                  {/* INFORMATION BOX */}
                  <Paper sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                      INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      This portal provides official services for the verification of primary education examination results and access to school administrative functions. All data is sourced directly from the Ministry of Primary Education records.
                    </Typography>
                  </Paper>

                  {/* QUICK LINKS BOX */}
                  <Paper sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                      QUICK LINKS
                    </Typography>
                    <Stack spacing={1}>
                      <MuiLink 
                        href="#" 
                        sx={{ 
                          color: "#1d4ed8", 
                          textDecoration: "underline", 
                          display: "block",
                          fontSize: { xs: "0.813rem", sm: "0.875rem" },
                        }}
                      >
                        Ministry Official Website
                      </MuiLink>
                      <MuiLink 
                        href="#" 
                        sx={{ 
                          color: "#1d4ed8", 
                          textDecoration: "underline", 
                          display: "block",
                          fontSize: { xs: "0.813rem", sm: "0.875rem" },
                        }}
                      >
                        Delta State Government Portal
                      </MuiLink>
                    </Stack>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          </Container>

          {/* FOOTER */}
          <Box 
            component="footer" 
            sx={{ 
              bgcolor: "primary.main", 
              color: "#d1d5db", 
              py: { xs: 4, md: 6 },
              mt: "auto",
            }}
          >
            <Container maxWidth="lg">
              <Grid 
                container 
                spacing={isMobile ? 3 : 4} 
                sx={{ mb: { xs: 3, md: 4 } }}
              >
                <Grid item xs={12} md={4}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: "#ffffff", 
                      fontWeight: 600, 
                      mb: 2,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    MINISTRY OF PRIMARY EDUCATION
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                    Delta State Government
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                    Ministry of Primary Education
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    Asaba, Delta State, Nigeria
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: "#ffffff", 
                      fontWeight: 600, 
                      mb: 2,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    CONTACT INFORMATION
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                    Email: info@dsgmope.ng
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                    Address: Ministry Secretariat, Asaba
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    Telephone: +234 (0)901 234 5678
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: "#ffffff", 
                      fontWeight: 600, 
                      mb: 2,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    OFFICIAL RESOURCES
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <FooterLink 
                      href="#"
                      sx={{ fontSize: { xs: "0.813rem", sm: "0.875rem" } }}
                    >
                      www.dsgmope.ng
                    </FooterLink>
                  </Typography>
                  <Typography variant="body2">
                    <FooterLink 
                      href="#"
                      sx={{ fontSize: { xs: "0.813rem", sm: "0.875rem" } }}
                    >
                      Delta State Government Portal
                    </FooterLink>
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: { xs: 2, md: 3 } }} />

              <Typography
                variant="body2"
                align="center"
                sx={{ 
                  color: "rgba(255,255,255,0.7)", 
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  maxWidth: 900, 
                  mx: "auto", 
                  mb: 2,
                  px: { xs: 2, md: 0 },
                }}
              >
                OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
              </Typography>

              <Typography 
                variant="body2" 
                align="center" 
                sx={{ 
                  color: "rgba(255,255,255,0.7)", 
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                }}
              >
                © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
              </Typography>
            </Container>
          </Box>
        </ContentWrapper>
      </Box>
    </ThemeProvider>
  );
};

export default DeltaGovPortal;